/* eslint-disable @typescript-eslint/no-unused-vars */
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControlLabel, Switch } from '@mui/material';
import { useMemo } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { RecursiveKeyOf } from '../../../../../../utils/types';
import { CondominiumForm, schema } from '../../../../../../validations/condominium.validation';
import states from '../../../../states';

import useStyles from './styles';

import { Button, Autocomplete, Input, Upload } from '../../../../../../components';
import { Colors, CondominiumType, Condominium, Laundry } from '../../../../../../entities';
import ConfigurationPanel from '../Configurations';
import Furniture from '../../../../../../entities/furniture.entity';
import Cleaning from '../../../../../../entities/cleaning.entity';
import Maid from '../../../../../../entities/maid.entity';

interface FormProps {
  condominium: Condominium;
  laundries: Laundry[];
  furnitures: Furniture[];
  cleanings: Cleaning[];
  maids: Maid[];
  fileToUpload?: File | null;
  handleFieldChange: (target: RecursiveKeyOf<Condominium>, value: string | boolean | number | null) => void;
  handleUpload: (file: File | null) => void;
  onSubmit: () => void;
}

export const Form = ({
  condominium,
  laundries,
  furnitures,
  cleanings,
  maids,
  fileToUpload,
  handleFieldChange,
  onSubmit,
  handleUpload,
}: FormProps): JSX.Element => {
  const styles = useStyles();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CondominiumForm>({
    resolver: yupResolver(schema),
    defaultValues: condominium,
    mode: 'onChange',
  });

  const getValueFromState = useMemo(
    () => states.find((e) => e.code === condominium.fullAddress?.stateCode) || null,
    [condominium.fullAddress?.stateCode]
  );

  const validationRegister = (name: RecursiveKeyOf<CondominiumForm>) =>
    register(name as keyof CondominiumForm, { onChange: (e) => handleFieldChange(name, e.target.value) });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.content}>
        <div className={styles.left}>
          <Upload file={fileToUpload || condominium?.avatarUrl} onUpload={handleUpload} />
        </div>
        <div className={styles.right}>
          <div className={styles.line}>
            <Input
              label="nome do condominío"
              placeholder="Ex: Condomínio 01"
              validationRegister={validationRegister('name')}
              fullWidth
              error={Boolean(errors.name)}
              helperText={errors.name?.message || ' '}
            />
            <Input
              label="cnpj"
              control={control as unknown as Control}
              validationRegister={validationRegister('cnpj')}
              placeholder="Ex: 00.000.000/0000-00"
              sx={{ width: '55%' }}
              mask={{ mask: '00.000.000/0000-00' }}
              error={Boolean(errors.cnpj)}
              helperText={errors.cnpj?.message || ' '}
            />
            <Input
              label="código de vínculo"
              control={control as unknown as Control}
              validationRegister={validationRegister('bondCode')}
              placeholder="Ex: A1A2A3"
              sx={{ width: '55%' }}
              inputProps={{ style: { textTransform: 'uppercase' } }}
              error={Boolean(errors.bondCode)}
              helperText={errors.bondCode?.message || ' '}
            />
          </div>

          <div className={styles.line}>
            <Input
              label="endereço"
              placeholder="Ex: Alameda dos Anjos"
              validationRegister={validationRegister('fullAddress.streetName')}
              fullWidth
              error={Boolean(errors.fullAddress?.streetName)}
              helperText={errors.fullAddress?.streetName?.message || ' '}
            />

            <Input
              label="bairro"
              placeholder="Ex: Jardins"
              validationRegister={validationRegister('fullAddress.neighborhood')}
              fullWidth
              error={Boolean(errors.fullAddress?.neighborhood)}
              helperText={errors.fullAddress?.neighborhood?.message || ' '}
            />

            <Input
              label="numero"
              placeholder="Ex: 123"
              validationRegister={validationRegister('fullAddress.buildingNumber')}
              sx={{ width: '30%' }}
              error={Boolean(errors.fullAddress?.buildingNumber)}
              helperText={errors.fullAddress?.buildingNumber?.message || ' '}
            />
            <Input
              label="cep"
              placeholder="Ex: 00000-000"
              mask={{ mask: '00000-000' }}
              autoComplete="off"
              sx={{ width: '50%' }}
              validationRegister={validationRegister('fullAddress.postalCode')}
              control={control as unknown as Control}
              error={Boolean(errors.fullAddress?.postalCode)}
              helperText={errors.fullAddress?.postalCode?.message || ' '}
            />
          </div>

          <div className={styles.line}>
            <Input
              label="cidade"
              placeholder="Ex: São Paulo"
              validationRegister={validationRegister('fullAddress.city')}
              fullWidth
              error={Boolean(errors.fullAddress?.city)}
              helperText={errors.fullAddress?.city?.message || ' '}
            />
            <Autocomplete
              label="estado"
              placeholder="Ex: SP"
              value={getValueFromState}
              options={states}
              getOptionLabel={(option) => option.name}
              sx={{ width: '30%' }}
              onChange={(event, data) => {
                handleFieldChange('fullAddress.stateCode', data?.code!);
                setValue('fullAddress.stateCode', data?.code);
                trigger('fullAddress.stateCode');
              }}
              error={Boolean(errors.fullAddress?.stateCode)}
              helperText={errors.fullAddress?.stateCode?.message || ' '}
            />
            <Input
              label="telefone"
              autoComplete="off"
              placeholder="Ex: (00) 00000-0000"
              sx={{ width: '30%' }}
              mask={{ mask: [{ mask: '(00) 0000-0000' }, { mask: '(00) 00000-0000' }] }}
              control={control as unknown as Control}
              validationRegister={validationRegister('phone')}
              error={Boolean(errors.phone)}
              helperText={errors.phone?.message || ' '}
            />
          </div>

          <div className={styles.line}>
            <Input
              label="nome do sindico"
              placeholder="Ex: João"
              validationRegister={validationRegister('syndicName')}
              fullWidth
              helperText={' '}
            />

            <Input
              label="Telefone do sindico"
              placeholder="Ex: (00) 00000-0000"
              validationRegister={validationRegister('syndicPhone')}
              control={control as unknown as Control}
              mask={{ mask: [{ mask: '(00) 0000-0000' }, { mask: '(00) 00000-0000' }] }}
              fullWidth
              helperText={' '}
            />

            <Input
              label="email de contato"
              placeholder="Ex: joao@gmail.com"
              validationRegister={validationRegister('contactEmail')}
              fullWidth
              helperText={' '}
            />
          </div>

          <div className={styles.line}>
            <Input
              label="nome do zelador"
              placeholder="Ex: José"
              validationRegister={validationRegister('caretakerName')}
              fullWidth
              helperText={' '}
            />

            <Input
              label="Telefone do zelador"
              placeholder="Ex: (00) 00000-0000"
              mask={{ mask: [{ mask: '(00) 0000-0000' }, { mask: '(00) 00000-0000' }] }}
              control={control as unknown as Control}
              validationRegister={validationRegister('caretakerPhone')}
              fullWidth
              helperText={' '}
            />
          </div>
          <ConfigurationPanel
            condominium={condominium}
            laundries={laundries}
            furnitures={furnitures}
            cleanings={cleanings}
            maids={maids}
            handleFieldChange={handleFieldChange}
            setValue={setValue}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <Button sx={{ width: '200px' }} loading={isSubmitting} type="submit">
          Salvar
        </Button>
      </div>
    </form>
  );
};

export default Form;
